// Crew roster — workers, classifications, wage + fringe rates.
// Drives WH-347 Davis-Bacon weekly payroll forms (per crew × project × week).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type Crew = {
  id: string;
  plant_id: string;
  name: string;
  classification: string;
  wage_rate_per_hour: string | number;
  fringe_per_hour: string | number;
  certifications: Record<string, string>;
  active: boolean;
  notes: string | null;
  is_sample: boolean;
  created_at: string;
  updated_at: string;
};

const CLASSIFICATIONS = [
  'Lineman',
  'Apprentice Lineman',
  'Splicer',
  'Equipment Operator',
  'Truck Driver',
  'Laborer',
  'Foreman',
  'Locator',
  'Restoration',
  'Drone / Survey',
  'Other',
];

// Canonical certification list rendered in the edit modal + the table cell.
// Storage key (DB JSONB) → human label + what the date represents.
// Keys must stay snake_case to match WH-347/Davis-Bacon serializers downstream.
type CertDef = { key: string; label: string; help: string };
const CERT_DEFS: CertDef[] = [
  { key: 'osha_30',         label: 'OSHA 30-hour Construction', help: 'Card date' },
  { key: 'osha_10',         label: 'OSHA 10-hour Construction', help: 'Card date' },
  { key: 'e_verify',        label: 'E-Verify',                  help: 'Verified date' },
  { key: 'cdl',             label: 'CDL (Commercial Driver)',   help: 'Expires' },
  { key: 'first_aid',       label: 'First Aid / CPR',           help: 'Expires' },
  { key: 'confined_space',  label: 'Confined Space',            help: 'Expires' },
  { key: 'bucket_truck',    label: 'Bucket Truck / Aerial Lift', help: 'Cert date' },
  { key: 'apprentice_reg',  label: 'DOL Apprenticeship #',       help: 'Reg #' },
];

// Apprenticeship registration is a string ID, not a date. Treat it specially.
const STRING_CERTS = new Set(['apprentice_reg']);

function certLabel(key: string): string {
  const d = CERT_DEFS.find((x) => x.key === key);
  return d?.label ?? key.replace(/_/g, ' ');
}

const fmtUSD = (v: any) => {
  const n = Number(v); return isFinite(n) ? `$${n.toFixed(2)}` : '—';
};

export default function CrewsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ items: Crew[] }>({
    queryKey: ['crews'],
    queryFn: () => api.get('/crews'),
  });
  const items = data?.items ?? [];

  const [editing, setEditing] = useState<Crew | null>(null);
  const [creating, setCreating] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const filtered = items.filter((c) => showInactive || c.active);
  const sampleCount = items.filter((c) => c.is_sample).length;

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">Crews</h1>
          <div className="page__subtitle">
            Workers and their wage rates (drives WH-347 payroll), plus the field crews work orders are assigned to.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
          <button className="btn btn--primary" onClick={() => setCreating(true)}>+ New crew member</button>
        </div>
      </div>

      {sampleCount > 0 && (
        <div style={{
          background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e',
          padding: '6px 10px', borderRadius: 4, fontSize: 11, marginBottom: 12,
        }}>
          <strong>{sampleCount} sample worker{sampleCount === 1 ? '' : 's'}</strong> — clear via Settings → Sample Data when going to production.
        </div>
      )}

      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ textAlign: 'left',  padding: '8px 12px', fontWeight: 600, color: '#475569' }}>Name</th>
              <th style={{ textAlign: 'left',  padding: '8px 12px', fontWeight: 600, color: '#475569' }}>Classification</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: '#475569', width: 110 }}>Wage / hr</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: '#475569', width: 110 }}>Fringe / hr</th>
              <th style={{ textAlign: 'left',  padding: '8px 12px', fontWeight: 600, color: '#475569' }}>Certifications</th>
              <th style={{ textAlign: 'left',  padding: '8px 12px', fontWeight: 600, color: '#475569', width: 100 }}>Status</th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                No crew members yet. Click "+ New crew member" to add one.
              </td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: c.active ? 1 : 0.55 }}>
                <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                  {c.name}
                  {c.is_sample && (
                    <span style={{ marginLeft: 6, padding: '1px 6px', background: '#fef3c7', color: '#92400e', borderRadius: 3, fontSize: 9, fontWeight: 700 }}>SAMPLE</span>
                  )}
                </td>
                <td style={{ padding: '8px 12px', color: '#475569' }}>{c.classification}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(c.wage_rate_per_hour)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(c.fringe_per_hour)}</td>
                <td style={{ padding: '8px 12px', fontSize: 11, color: '#64748b' }}>
                  {Object.entries(c.certifications ?? {})
                    .filter(([, v]) => v != null && String(v).length > 0)
                    .map(([k, v]) => (
                      <span key={k} style={{
                        display: 'inline-block', marginRight: 4, marginBottom: 2, padding: '1px 6px',
                        background: '#dbeafe', color: '#1e40af', borderRadius: 3, fontSize: 10, fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}>{certLabel(k)} <span style={{ fontWeight: 400, color: '#3b82f6' }}>{String(v)}</span></span>
                    ))}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700,
                    background: c.active ? '#dcfce7' : '#f1f5f9',
                    color:      c.active ? '#065f46' : '#64748b',
                  }}>{c.active ? 'ACTIVE' : 'INACTIVE'}</span>
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                  <button
                    onClick={() => setEditing(c)}
                    style={{ padding: '3px 10px', fontSize: 11, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 3, cursor: 'pointer' }}
                  >Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DispatchCrews />

      {(editing || creating) && (
        <CrewEditModal
          initial={editing ?? undefined}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['crews'] });
          }}
        />
      )}
    </>
  );
}

function CrewEditModal({
  initial, onClose, onSaved,
}: {
  initial?: Crew;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !initial;
  const [name, setName] = useState(initial?.name ?? '');
  const [classification, setClassification] = useState(initial?.classification ?? 'Laborer');
  const [wage, setWage] = useState<string>(String(initial?.wage_rate_per_hour ?? '27.50'));
  const [fringe, setFringe] = useState<string>(String(initial?.fringe_per_hour ?? '5.90'));
  const [active, setActive] = useState(initial?.active ?? true);
  const [notes, setNotes] = useState(initial?.notes ?? '');

  // Structured certs: one input per known cert key. Stored back to the DB
  // as a flat JSONB object {[key]: dateOrString}, same shape as before.
  // Empty inputs are dropped on save so we don't persist {} entries.
  const [certs, setCerts] = useState<Record<string, string>>(
    () => ({ ...(initial?.certifications ?? {}) })
  );
  const setCert = (k: string, v: string) =>
    setCerts((prev) => ({ ...prev, [k]: v }));

  const saveMut = useMutation({
    mutationFn: () => {
      // Drop empty entries so the DB record is clean
      const cleanCerts: Record<string, string> = {};
      for (const [k, v] of Object.entries(certs)) {
        if (v != null && String(v).trim().length > 0) {
          cleanCerts[k] = String(v).trim();
        }
      }
      const body = {
        name, classification,
        wage_rate_per_hour: Number(wage),
        fringe_per_hour: Number(fringe),
        certifications: cleanCerts,
        active,
        notes: notes || null,
      };
      return isNew
        ? api.post('/crews', body)
        : api.put(`/crews/${initial!.id}`, body);
    },
    onSuccess: onSaved,
  });

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 720, maxHeight: '90vh', overflow: 'auto', background: '#fff', borderRadius: 8, boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}
      >
        <div style={{ padding: '12px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{isNew ? 'New crew member' : `Edit · ${initial!.name}`}</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: 18, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>
        <div style={{ padding: 18, display: 'grid', gridTemplateColumns: '140px 1fr', gap: '10px 12px', alignItems: 'baseline' }}>
          <label style={{ fontSize: 12, color: '#64748b' }}>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)}
            style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }} />

          <label style={{ fontSize: 12, color: '#64748b' }}>Classification</label>
          <select value={classification} onChange={(e) => setClassification(e.target.value)}
            style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }}>
            {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          <label style={{ fontSize: 12, color: '#64748b' }}>Wage rate ($/hr)</label>
          <input type="number" min={0} step={0.01} value={wage} onChange={(e) => setWage(e.target.value)}
            style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3, width: 120 }} />

          <label style={{ fontSize: 12, color: '#64748b' }}>Fringe ($/hr)</label>
          <input type="number" min={0} step={0.01} value={fringe} onChange={(e) => setFringe(e.target.value)}
            style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3, width: 120 }} />

          <label style={{ fontSize: 12, color: '#64748b', alignSelf: 'start', paddingTop: 6 }}>Certifications</label>
          <div style={{
            border: '1px solid #e2e8f0', borderRadius: 4, padding: 10,
            background: '#fafbfc',
            display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14, rowGap: 6,
          }}>
            {CERT_DEFS.map((d) => {
              const isString = STRING_CERTS.has(d.key);
              const v = certs[d.key] ?? '';
              return (
                <div key={d.key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '.03em' }}>
                    {d.label}
                    <span style={{ fontWeight: 400, color: '#94a3b8', textTransform: 'none', marginLeft: 4 }}>· {d.help}</span>
                  </label>
                  <input
                    type={isString ? 'text' : 'date'}
                    value={v}
                    onChange={(e) => setCert(d.key, e.target.value)}
                    placeholder={isString ? 'OA-2024-12345' : ''}
                    style={{ padding: '4px 6px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 3 }}
                  />
                </div>
              );
            })}
          </div>

          <label style={{ fontSize: 12, color: '#64748b' }}>Active</label>
          <label style={{ fontSize: 12 }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span style={{ marginLeft: 6 }}>Currently working</span>
          </label>

          <label style={{ fontSize: 12, color: '#64748b' }}>Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            style={{ padding: '5px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 3, fontFamily: 'inherit' }} />
        </div>
        <div style={{ padding: '10px 18px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {saveMut.isError && <span style={{ color: '#b91c1c', fontSize: 11, alignSelf: 'center' }}>Save failed: {(saveMut.error as any)?.message ?? 'unknown'}</span>}
          <button onClick={onClose}
            style={{ padding: '6px 14px', fontSize: 12, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 3, cursor: 'pointer' }}>Cancel</button>
          <button
            disabled={!name || !classification || saveMut.isPending}
            onClick={() => saveMut.mutate()}
            style={{ padding: '6px 16px', fontSize: 12, fontWeight: 600, background: '#1e3a8a', color: '#fff', border: 0, borderRadius: 3, cursor: 'pointer' }}
          >
            {saveMut.isPending ? 'Saving…' : (isNew ? 'Create' : 'Save changes')}
          </button>
        </div>
      </div>
    </div>
  );
}


// --- Dispatch crews -------------------------------------------------------
// `crews` is a different object from the worker roster above: it is the field TEAM a
// work order gets assigned to, typed by the work it does. Work orders and schedule
// tasks both point here, so this list must be user-manageable — deriving it from the
// roster (one crew per worker) would be wrong, and seeding it with invented crews
// would put data in a customer's database that they never entered.
type DispatchCrew = {
  id: string;
  name: string;
  type: string;
  color: string;
  vendor: string | null;
  foreman_id: string | null;
  foreman_name: string | null;
  is_active: boolean;
  work_order_count: number;
};

const DISPATCH_TYPES = ['construction', 'splicing', 'locating', 'restoration', 'survey'];

function DispatchCrews() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ items: DispatchCrew[] }>({
    queryKey: ['dispatch-crews'],
    queryFn: () => api.get('/crews/dispatch'),
  });
  const items = data?.items ?? [];

  const [name, setName] = useState('');
  const [type, setType] = useState('construction');
  const [vendor, setVendor] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.post('/crews/dispatch', { name, type, vendor: vendor || null }),
    onSuccess: () => {
      setName(''); setVendor(''); setErr(null);
      qc.invalidateQueries({ queryKey: ['dispatch-crews'] });
      qc.invalidateQueries({ queryKey: ['work-order-crews'] });
    },
    onError: (e: any) => setErr(e?.message ?? 'Could not create crew'),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.del('/crews/dispatch/' + id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatch-crews'] });
      qc.invalidateQueries({ queryKey: ['work-order-crews'] });
    },
  });

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ marginBottom: 10 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Field crews</h2>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
          The crews you assign work orders to. Add one per team you dispatch.
        </div>
      </div>

      <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 11, color: '#475569' }}>
          <div style={{ marginBottom: 3 }}>Crew name</div>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="North Line Crew"
            style={{ padding: '5px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 3, width: 190 }} />
        </label>
        <label style={{ fontSize: 11, color: '#475569' }}>
          <div style={{ marginBottom: 3 }}>Work type</div>
          <select value={type} onChange={(e) => setType(e.target.value)}
            style={{ padding: '5px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 3 }}>
            {DISPATCH_TYPES.map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 11, color: '#475569' }}>
          <div style={{ marginBottom: 3 }}>Contractor (optional)</div>
          <input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="In-house"
            style={{ padding: '5px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 3, width: 160 }} />
        </label>
        <button className="btn btn--primary" disabled={!name || create.isPending}
          onClick={() => create.mutate()}>+ Add field crew</button>
        {err && <span style={{ fontSize: 11, color: '#b91c1c' }}>{err}</span>}
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#475569' }}>Crew</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#475569' }}>Work type</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#475569' }}>Contractor</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: '#475569', width: 110 }}>Work orders</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#475569', width: 100 }}>Status</th>
              <th style={{ width: 110 }}></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                No field crews yet. Add one above — work orders can't be assigned until you do.
              </td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: c.is_active ? 1 : 0.55 }}>
                <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 8, background: c.color, marginRight: 7 }} />
                  {c.name}
                </td>
                <td style={{ padding: '8px 12px', color: '#475569' }}>{c.type}</td>
                <td style={{ padding: '8px 12px', color: '#475569' }}>{c.vendor ?? 'In-house'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.work_order_count}</td>
                <td style={{ padding: '8px 12px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700,
                    background: c.is_active ? '#dcfce7' : '#f1f5f9',
                    color:      c.is_active ? '#065f46' : '#64748b',
                  }}>{c.is_active ? 'ACTIVE' : 'INACTIVE'}</span>
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                  {c.is_active && (
                    <button onClick={() => deactivate.mutate(c.id)}
                      style={{ padding: '3px 10px', fontSize: 11, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 3, cursor: 'pointer' }}
                    >Deactivate</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
